import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, Typography, Box, Paper, List, ListItem, 
  ListItemText, ListItemAvatar, Avatar, Divider, Button,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, TextField, Tabs, Tab,
  Tooltip
} from '@mui/material';
import { 
  Forum as ForumIcon, 
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  ClearAll as ClearAllIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { chatsAPI } from '../api/api';
import axios from 'axios';

// Компонент панели в диалоге диагностики
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`diagnostics-tabpanel-${index}`}
      aria-labelledby={`diagnostics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ChatsPage = () => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  const [telegramId, setTelegramId] = useState('');
  const [idTestResult, setIdTestResult] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  
  // Извлекаем ID из локального хранилища для автозаполнения
  const userIdRef = useRef('');
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        if (userData.id) {
          userIdRef.current = userData.id.toString();
          setTelegramId(userData.id.toString());
        }
      }
    } catch (err) {
      console.error('Ошибка извлечения ID пользователя:', err);
    }
  }, []);

  // Загрузка списка чатов
  const fetchChats = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`ChatsPage: Загрузка списка чатов... (forceRefresh=${forceRefresh})`);
      const response = await chatsAPI.getChats(forceRefresh);
      console.log('ChatsPage: Получен ответ:', response.data);
      
      // Проверяем что данные корректны и преобразуем в массив если нужно
      const chatsList = response.data?.chats || [];
      console.log('ChatsPage: Обработанный список чатов:', chatsList);
      setChats(Array.isArray(chatsList) ? chatsList : []);
    } catch (err) {
      console.error('ChatsPage: Ошибка загрузки чатов:', err);
      setError('Не удалось загрузить список чатов: ' + (err.message || 'Неизвестная ошибка'));
      // Устанавливаем пустой массив чатов при ошибке
      setChats([]);
    } finally {
      setLoading(false);
    }
  };

  // Загружаем чаты при монтировании компонента
  useEffect(() => {
    fetchChats();
  }, []);

  // Обработчик нажатия на чат
  const handleChatClick = (chatId) => {
    navigate(`/chats/${chatId}`);
  };
  
  // Обработчик обновления списка чатов
  const handleRefresh = () => {
    fetchChats(true);
  };
  
  // Обработчик принудительного сброса кеша сервера
  const handleForceRefresh = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await chatsAPI.forceRefresh();
      // После сброса кеша и обновления списка чатов
      fetchChats(true);
    } catch (err) {
      console.error('ChatsPage: Ошибка сброса кеша сервера:', err);
      setError('Не удалось сбросить кеш сервера: ' + (err.message || 'Неизвестная ошибка'));
    }
  };
  
  // Переключение вкладок диагностики
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Запуск диагностики статуса администратора
  const handleDiagnostic = async () => {
    setDiagnosing(true);
    setDiagnosticData(null);
    setDiagnosticOpen(true);
    
    try {
      // Получаем только публичную отладочную информацию без авторизации
      const debugResponse = await axios.get('/api/debug-status');
      const debugInfo = debugResponse.data;
      console.log('Получена отладочная информация:', debugInfo);
      
      // Информация о пользователе из localStorage
      let userInfo = null;
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          userInfo = JSON.parse(storedUser);
        }
      } catch (err) {
        console.error('Ошибка получения данных пользователя:', err);
      }
      
      // Объединяем результаты
      setDiagnosticData({
        debug_info: debugInfo,
        user_info: userInfo,
        local_storage: {
          has_token: !!localStorage.getItem('token'),
          has_user: !!localStorage.getItem('user')
        },
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      console.error('Ошибка диагностики:', err);
      setDiagnosticData({ 
        error: err.message || 'Неизвестная ошибка',
        timestamp: new Date().toISOString()
      });
    } finally {
      setDiagnosing(false);
    }
  };
  
  // Обработчик тестирования ID
  const handleTestId = async () => {
    if (!telegramId.trim()) {
      alert('Введите Telegram ID');
      return;
    }
    
    try {
      setDiagnosing(true);
      const response = await axios.get(`/api/test-id?id=${telegramId.trim()}`);
      console.log('Результат проверки ID:', response.data);
      setIdTestResult(response.data);
    } catch (err) {
      console.error('Ошибка при проверке ID:', err);
      setIdTestResult({
        error: err.response?.data?.error || err.message || 'Неизвестная ошибка'
      });
    } finally {
      setDiagnosing(false);
    }
  };
  
  // Закрытие диалога диагностики
  const handleCloseDiagnostic = () => {
    setDiagnosticOpen(false);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2
        }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Доступные чаты
            </Typography>
            <Typography variant="body1">
              Выберите чат для управления настройками бота
            </Typography>
          </Box>
          
          <Box>
            <Button 
              variant="outlined" 
              color="secondary"
              startIcon={<BugIcon />}
              onClick={handleDiagnostic}
              sx={{ mr: 1 }}
              disabled={loading || diagnosing}
            >
              Диагностика
            </Button>
            
            <Tooltip title="Полное обновление списка чатов и прав доступа">
              <Button 
                variant="outlined" 
                color="warning"
                startIcon={<ClearAllIcon />}
                onClick={handleForceRefresh}
                sx={{ mr: 1 }}
                disabled={loading || diagnosing}
              >
                Сбросить кеш
              </Button>
            </Tooltip>
            
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading || diagnosing}
            >
              Обновить список
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : !chats || chats.length === 0 ? (
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography gutterBottom>
              Нет доступных чатов. Убедитесь, что вы являетесь администратором чата, в котором установлен бот.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Если вы недавно были назначены администратором, нажмите на кнопку "Обновить список" для обновления данных.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Для выявления проблем с правами доступа, используйте кнопку "Диагностика".
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={2} sx={{ mt: 2 }}>
            <List>
              {chats.map((chat, index) => (
                <React.Fragment key={chat?.id || index}>
                  {index > 0 && <Divider variant="inset" component="li" />}
                  <ListItem
                    alignItems="flex-start"
                    button
                    onClick={() => handleChatClick(chat?.id)}
                  >
                    <ListItemAvatar>
                      <Avatar alt={chat?.title || 'Чат'} sx={{ bgcolor: 'primary.main' }}>
                        <ForumIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={chat?.title || 'Безымянный чат'}
                      secondary={`ID: ${chat?.id || 'Н/Д'} • Тип: ${chat?.type === 'supergroup' ? 'Супергруппа' : 'Группа'}`}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}
        
        {/* Диалог диагностики */}
        <Dialog 
          open={diagnosticOpen} 
          onClose={handleCloseDiagnostic}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            Диагностика статуса администратора
            <IconButton
              aria-label="close"
              onClick={handleCloseDiagnostic}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="diagnostic tabs">
              <Tab label="Общая информация" />
              <Tab label="Проверка ID" />
            </Tabs>
            
            <TabPanel value={activeTab} index={0}>
              {diagnosing ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : diagnosticData ? (
                <Box 
                  component="pre" 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'background.paper', 
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '400px'
                  }}
                >
                  {JSON.stringify(diagnosticData, null, 2)}
                </Box>
              ) : (
                <Typography color="error">Нет данных диагностики</Typography>
              )}
            </TabPanel>
            
            <TabPanel value={activeTab} index={1}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Проверьте права администратора для конкретного Telegram ID:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TextField 
                    fullWidth
                    label="Telegram ID"
                    value={telegramId}
                    onChange={(e) => setTelegramId(e.target.value)}
                    disabled={diagnosing}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTestId}
                    disabled={diagnosing}
                    startIcon={<SearchIcon />}
                  >
                    Проверить
                  </Button>
                </Box>
                
                {diagnosing ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : idTestResult ? (
                  <Box 
                    component="pre" 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'background.paper', 
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '400px'
                    }}
                  >
                    {JSON.stringify(idTestResult, null, 2)}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Результаты проверки будут отображены здесь
                  </Typography>
                )}
              </Box>
            </TabPanel>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDiagnostic}>Закрыть</Button>
            {activeTab === 0 && (
              <Button 
                onClick={handleDiagnostic} 
                disabled={diagnosing}
                color="primary"
              >
                Повторить диагностику
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ChatsPage; 